import React, { useState } from 'react';
import ClanSelector from '../components/shark/ClanSelector';
import { api, logHttpError, storeAccessToken } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface RegisterPageProps {
  onLoginSuccess: (id: string, isAdmin: boolean) => void;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    fullName: '',
    birthDate: '',
  });

  const [selectedClanId, setSelectedClanId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleRegister = async () => {
    if (!selectedClanId || isNaN(selectedClanId)) {
      setError("Выбери стаю!");
      return;
    }

    if (!formData.username || !formData.password || !formData.email ||
        !formData.fullName || !formData.birthDate) {
      setError("Заполни все поля!");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Пароли не совпадают!");
      return;
    }

    setIsLoading(true);
    try {
      const userData = {
        username: formData.username,
        password: formData.password,
        clan_id: selectedClanId,
        email: formData.email,
        full_name: formData.fullName,
        birth_date: formData.birthDate,
      };

      const response = await api.post('/auth/register', userData);

      const userId = response.data.id.toString();
      const isAdmin = response.data.is_admin || false;
      const accessToken = response.data.access_token;

      if (!accessToken) {
        throw new Error('Сервер не вернул токен доступа');
      }

      localStorage.setItem('sharkly_user_id', userId);
      localStorage.setItem('sharkly_is_admin', isAdmin.toString());
      storeAccessToken(accessToken);

      onLoginSuccess(userId, isAdmin);
      navigate('/feed');

    } catch (err: any) {
      logHttpError("Ошибка регистрации", err);
      setError(err.response?.data?.detail || "Ошибка регистрации");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 bg-[#0f1115] overflow-y-auto">
      <div className="mb-6 text-center">
        <h1 className="text-4xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 uppercase">
          SHARKLY 🦈
        </h1>
        <p className="text-gray-400 text-xs mt-1">Создай свой профиль</p>
      </div>

      <div className="w-full max-w-md space-y-3 mb-8">
        <input name="username" placeholder="Никнейм" value={formData.username} onChange={handleChange}
               className="w-full p-3 rounded-lg bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none" />

        <div className="grid grid-cols-2 gap-3">
           <input name="password" type="password" placeholder="Пароль" value={formData.password} onChange={handleChange}
                  className="w-full p-3 rounded-lg bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none" />
           <input name="confirmPassword" type="password" placeholder="Повторите пароль" value={formData.confirmPassword} onChange={handleChange}
                  className="w-full p-3 rounded-lg bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none" />
        </div>

        <input name="email" type="email" placeholder="Email" value={formData.email} onChange={handleChange}
               className="w-full p-3 rounded-lg bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none" />

        <input name="fullName" placeholder="Фамилия Имя Отчество" value={formData.fullName} onChange={handleChange}
               className="w-full p-3 rounded-lg bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none" />

        <div className="relative">
            <label className="absolute left-3 -top-2.5 bg-[#0f1115] px-1 text-xs text-gray-400">Дата рождения</label>
            <input name="birthDate" type="date" value={formData.birthDate} onChange={handleChange}
                   className="w-full p-3 rounded-lg bg-[#1a1d24] border border-gray-700 text-white focus:border-blue-500 outline-none [color-scheme:dark]" />
        </div>
      </div>

      <div className="w-full max-w-3xl mb-8">
        <h2 className="text-center text-gray-300 font-semibold mb-4 uppercase tracking-widest text-xs">
          Выбери свою стаю
        </h2>
        <ClanSelector onSelect={(id) => setSelectedClanId(Number(id))} />
      </div>

      {error && <p className="text-red-500 text-sm mb-4 text-center">{error}</p>}

      <button onClick={handleRegister} disabled={isLoading}
              className={`px-12 py-3 rounded-full font-bold transition-all shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:-translate-y-1 active:scale-95 ${isLoading ? 'bg-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'}`}>
        {isLoading ? 'Регистрация...' : 'Вступить в Sharkly'}
      </button>
    </div>
  );
};

export default RegisterPage;
